import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../components/Icon';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { DensityToggle } from '../../components/DensityToggle';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import { useInboundCount } from '../mail/queries';
import { SavedViewsBar } from '../shortcuts/SavedViewsBar';
import { SlaBadge } from '../sla/SlaBadge';
import { nameOf, useAssignableUsers, type AssignableUser } from '../users/queries';
import type { StateCategory, TicketListItem } from './api';
import { BulkBar } from './BulkBar';
import { useSession } from '../auth/queries';
import { CreateTicketDialog } from './CreateTicketDialog';
import { TicketCustomerFilter } from './TicketCustomerFilter';
import { DEFAULT_COLUMNS, DEFAULT_SORT, SORT_FIELD_OF, type ViewColumn } from './columns';
import { ColumnPicker } from './ColumnPicker';
import { STATE_CATEGORIES, toneForPriorityLevel, toneForStateCategory } from './presentation';
import { useTicketFilterCounts, useTicketList } from './queries';

/**
 * Welcher Reiter der Ticketliste gewaehlt ist.
 *
 * ``unresolved`` heisst "noch zu tun" -- alles ausser Geloest und
 * Geschlossen, dieselbe Abgrenzung, die die Kennzahl der Startseite zaehlt.
 * Es ist ausdruecklich **nicht** die Zustandsgruppe ``open`` ("In
 * Bearbeitung"): Die laesst ein frisch angelegtes Ticket aus.
 */
type TabValue = StateCategory | 'unresolved' | 'all';

/**
 * Die Reiter in ihrer Reihenfolge -- Issue #9.
 *
 * "Offen" zuerst und "Alle" zuletzt. Dazwischen die Zustandsgruppen in der
 * Ordnung, in der ein Vorgang sie durchlaeuft; sie stammt aus
 * ``STATE_CATEGORIES`` und wird hier nicht noch einmal aufgeschrieben --
 * zwei Listen liefen beim naechsten Zustand auseinander.
 */
const TABS: readonly TabValue[] = ['unresolved', ...STATE_CATEGORIES, 'all'];

/**
 * Die Ticketliste -- F1-30, F1-32, F1-40, F1-41.
 *
 * Geblaettert wird ueber einen Cursor und nicht ueber einen Versatz. Der
 * Unterschied faellt genau dann auf, wenn es darauf ankommt: Bei laufend
 * eingehenden Tickets verschoebe ein Versatz die Ergebnisse zwischen den
 * Seiten, und der Bearbeiter uebersaehe einen Vorgang, ohne es zu merken
 * (docs/05, Abschnitt 3).
 *
 * **Die Suche wird erst beim Absenden ausgefuehrt**, nicht bei jedem
 * Tastendruck. Eine Volltextsuche ist keine Filterung im Speicher -- sie
 * kostet den Server eine Abfrage ueber einen GIN-Index, und "Drucker" waere
 * sieben davon.
 *
 * **Die Liste beginnt auf "Offen" und nicht auf "Alle"** (Issue #9). Wer die
 * Ticketliste oeffnet, fragt "was liegt an" und nicht "was gab es je" -- und
 * bei einem gewachsenen Bestand steht die Antwort auf die erste Frage sonst
 * unter tausend geschlossenen Vorgaengen. "Alle" bleibt erreichbar und steht
 * am Ende der Reihe, wo man es sucht, wenn man es braucht.
 *
 * **Sortiert wird nach Anlage und nicht nach Aenderung.** Ein neu
 * angelegtes Ticket steht damit oben und bleibt dort; nach Aenderungszeit
 * rutschte es nach unten, sobald jemand einen aelteren Vorgang anfasste.
 * Die Reihenfolge laesst sich ueber die Ueberschriften umstellen -- aber nur
 * bei den Spalten, die der Server auch sortieren kann (siehe
 * ``SORT_FIELD_OF``).
 */
export function TicketListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Zustandsgruppe und Suchbegriff stehen in der Adresse und nicht im
  // Zustand der Seite: So laesst sich ein Filter als Lesezeichen behalten,
  // an einen Kollegen schicken, und die Zurueck-Taste stellt ihn wieder her.
  // Der Rest der Ansicht -- Spalten, Auswahl, Zeilenhoehe -- bleibt lokal:
  // Das sind Einstellungen des Betrachters und keine Aussage darueber,
  // welche Tickets gemeint sind.
  const {
    state: stateCategory,
    q: searchParam,
    owner,
    awaiting,
    all,
    sort: sortParam,
    customer,
  } = useSearch({ from: '/app/tickets' });
  const search = searchParam ?? '';
  const sort = sortParam ?? DEFAULT_SORT;

  // Welcher Reiter aktiv ist. Genau einer ist es immer -- ohne Angabe
  // "Offen". Das ist der Unterschied zu vorher: Die Schaltflaechen waren
  // Schalter, die sich alle ausschalten liessen, und "nichts gewaehlt" hiess
  // "alles". Jetzt ist "alles" eine Wahl wie jede andere.
  //
  // Die Adresse kann ``unresolved`` tragen (so verweisen die Kennzahlen der
  // Startseite hierher), aber sie muss es nicht: Ohne jede Angabe ist
  // ebenfalls "Offen" gemeint. Beide Wege fuehren deshalb auf denselben
  // Reiter, und nur ``all`` schaltet ihn ab.
  const activeTab: TabValue = all ? 'all' : (stateCategory ?? 'unresolved');

  // Ein Reiter setzt die Zustandseingrenzung und loescht dabei die beiden
  // anderen Ausdrucksformen. "Offen **und** geschlossen" waere eine leere
  // Liste, und wer beides angeklickt hat, sucht den Fehler bei sich.
  const setTab = (next: TabValue) => {
    void navigate({
      to: '/app/tickets',
      search: (current) => ({
        ...current,
        state: next === 'unresolved' || next === 'all' ? undefined : next,
        unresolved: next === 'unresolved' ? (true as const) : undefined,
        all: next === 'all' ? (true as const) : undefined,
      }),
      replace: true,
    });
  };

  /**
   * Stellt die Reihenfolge um.
   *
   * Ein Klick auf die bereits sortierte Spalte dreht die Richtung, ein Klick
   * auf eine andere beginnt absteigend. Absteigend zuerst, weil bei jedem
   * dieser Felder das Neueste, Groesste oder Dringendste oben erwartet wird
   * -- eine aufsteigende Ticketnummernliste beginnt beim ersten Vorgang von
   * 2026 und ist beim zweiten Klick da, wo man sie braucht.
   */
  const setSort = (field: string) => {
    const next = sort === `-${field}` ? field : `-${field}`;
    void navigate({
      to: '/app/tickets',
      search: (current) => ({ ...current, sort: next === DEFAULT_SORT ? undefined : next }),
      replace: true,
    });
  };

  const setSearch = (next: string) => {
    void navigate({
      to: '/app/tickets',
      search: (current) => ({ ...current, q: next === '' ? undefined : next }),
      replace: true,
    });
  };

  const [searchInput, setSearchInput] = useState(search);
  const [columns, setColumns] = useState<ViewColumn[]>([...DEFAULT_COLUMNS]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const session = useSession();
  const [selected, setSelected] = useState<string[]>([]);

  // Die Massenbearbeitung braucht dasselbe Recht wie eine einzelne Aenderung.
  // Das Ausblenden ist Bequemlichkeit -- der Server prueft ohnehin.
  const canWrite = (session.data?.permissions ?? []).includes('ticket.update');

  // Die Anfragen stehen neben der Ticketliste und nicht in der Navigation
  // (F3-01a): Was dort wartet, ist ein Vorgang, der noch keiner ist -- und
  // gesucht wird er dort, wo jemand ohnehin arbeitet. Die Blase ist der
  // ganze Sinn dieser Stelle: Eine Warteschlange, die man erst oeffnen muss,
  // um zu sehen, dass sie leer ist, wird nicht geoeffnet.
  const canTriage = (session.data?.permissions ?? []).includes('mail.triage');
  const waiting = useInboundCount(canTriage).data?.count ?? 0;

  // 'me' wird hier zur eigenen Kennung aufgeloest und nicht in der Adresse:
  // Die Kennung des Bearbeiters ist ein personenbezogenes Datum und hat im
  // Lesezeichen nichts zu suchen (siehe ``validateSearch`` im Router).
  const filters = {
    ...(activeTab !== 'unresolved' && activeTab !== 'all' ? { state_category: activeTab } : {}),
    ...(activeTab === 'unresolved' ? { unresolved: true } : {}),
    ...(search ? { q: search } : {}),
    ...(owner === 'me' && session.data ? { owner_id: session.data.user_id } : {}),
    ...(awaiting ? { awaiting_reply: true } : {}),
    ...(customer ? { customer_id: customer } : {}),
    // Bei einer Volltextsuche sortiert der Server nach Trefferguete und
    // uebergeht die Angabe -- sie mitzuschicken waere trotzdem falsch: Der
    // Zwischenspeicher unterschiede dann zwei Abfragen, die dasselbe
    // liefern.
    ...(search ? {} : { sort }),
  };

  /**
   * Dieselbe Einschraenkung, wie eine gespeicherte Ansicht sie fuehrt.
   *
   * **Ohne ``sort``**: Die Ansicht hat dafuer ein eigenes Feld, und die
   * Erlaubnisliste des Servers (``VIEW_FILTERS`` in
   * ``shortcuts/schemas.py``) kennt ``sort`` nicht -- er wiese das Speichern
   * ab. **Ohne ``owner_id``**: Das ist die Kennung des Betrachters; in einer
   * geteilten Ansicht gespeichert zeigte sie dem naechsten Leser die
   * Vorgaenge eines Fremden. **Ohne ``customer_id``**: Die Kundenauswahl ist
   * der Arbeitskontext (F1-56) und bleibt beim Anwenden einer Ansicht
   * stehen -- eine Ansicht, die bei gewaehltem Kunden gespeichert wird,
   * soll nicht stillschweigend an ihn gebunden sein.
   */
  const viewFilters = {
    ...(activeTab !== 'unresolved' && activeTab !== 'all' ? { state_category: activeTab } : {}),
    ...(activeTab === 'unresolved' ? { unresolved: true } : {}),
    ...(awaiting ? { awaiting_reply: true } : {}),
    ...(search ? { q: search } : {}),
  };

  // Kommt der Suchbegriff von aussen -- Lesezeichen, Kennzahlkachel,
  // Zurueck-Taste --, muss er auch im Eingabefeld stehen. Sonst zeigt die
  // Liste "Drucker" und das Feld daneben ist leer.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const users = useAssignableUsers();
  const query = useTicketList(filters);

  // Die Zahlen ueber den Reitern und an den beiden Schaltern. Jede sagt, wie
  // viele Zeilen ein Klick zeigen wuerde -- mit allem, was sonst gerade
  // eingegrenzt ist. Der Reiter selbst geht nicht in die Abfrage: Die Antwort
  // traegt alle Reiter auf einmal, und ein Wechsel laedt nichts nach.
  //
  // Mit "Meine Tickets" erst, wenn die eigene Kennung feststeht -- sonst
  // zaehlte die erste Abfrage die Tickets aller und die Zahl spraenge.
  const counts = useTicketFilterCounts(
    {
      ...(search ? { q: search } : {}),
      ...(owner === 'me' && session.data ? { owner_id: session.data.user_id } : {}),
      ...(awaiting ? { awaiting_reply: true } : {}),
      ...(customer ? { customer_id: customer } : {}),
    },
    { enabled: owner !== 'me' || session.data !== undefined },
  ).data;
  const tickets = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title={t('tickets.title')}
        count={
          tickets.length > 0
            ? query.hasNextPage
              ? t('tickets.countMore', { count: tickets.length })
              : t('tickets.count', { count: tickets.length })
            : undefined
        }
        action={
          <div className="row">
            {canTriage ? (
              <Link to="/app/anfragen" className="nav__item">
                {t('inbound.title')}
                {waiting > 0 ? (
                  <>
                    <span className="count-bubble" aria-hidden="true">
                      {waiting}
                    </span>
                    {/* Die Zahl allein sagt einem vorlesenden Programm nichts:
                        "Anfragen 7" koennte auch eine Ordnungsnummer sein. */}
                    <span className="visually-hidden">
                      {t('inbound.waiting', { count: waiting })}
                    </span>
                  </>
                ) : null}
              </Link>
            ) : null}
            {/* Die Kundenauswahl geht mit in die Kanban-Ansicht -- F1-56.
                Wer bei einem Kunden ist, wechselt die Sicht, nicht den Kunden. */}
            <Link to="/app/board" search={customer ? { customer } : {}} className="nav__item">
              {t('board.title')}
            </Link>
            <Button
              variant="primary"
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              {t('tickets.newTicket')}
            </Button>
          </div>
        }
      />

      <SavedViewsBar
        current={{ filters: viewFilters, columns, sort }}
        activeViewId={activeViewId}
        onApply={(view) => {
          setActiveViewId(view?.id ?? null);
          if (!view) {
            void navigate({
              to: '/app/tickets',
              search: customer ? { customer } : {},
              replace: true,
            });
            setColumns([...DEFAULT_COLUMNS]);
            return;
          }
          const gespeichert = view.filters as Record<string, string | boolean | undefined>;
          void navigate({
            to: '/app/tickets',
            search: {
              ...(gespeichert.state_category
                ? { state: gespeichert.state_category as StateCategory }
                : {}),
              ...(gespeichert.q ? { q: String(gespeichert.q) } : {}),
              // Ohne diese Zeile verloere eine gespeicherte Ansicht "meine
              // offenen Vorgaenge" beim Anwenden genau ihre Einschraenkung --
              // und zeigte den ganzen Bestand.
              ...(gespeichert.unresolved ? { unresolved: true as const } : {}),
              // Dasselbe fuer "Wartet auf uns" (F1-50): Ohne diese Zeile
              // zeigte die gespeicherte Arbeitsliste alle offenen Vorgaenge.
              ...(gespeichert.awaiting_reply ? { awaiting: true as const } : {}),
              // Eine Ansicht ohne Zustandsangabe meint **alle** Vorgaenge.
              // Ohne diese Zeile landete sie auf dem Standardreiter "Offen"
              // und zeigte weniger, als beim Speichern zu sehen war.
              ...(!gespeichert.state_category && !gespeichert.unresolved
                ? { all: true as const }
                : {}),
              ...(view.sort ? { sort: view.sort } : {}),
              // Die Kundenauswahl ist der Arbeitskontext und nicht Teil der
              // Ansicht (F1-56): "Meine offenen" bei Kunde X anzuwenden
              // heisst, meine offenen bei Kunde X zu sehen.
              ...(customer ? { customer } : {}),
            },
            replace: true,
          });
          setColumns(
            view.columns.length > 0 ? (view.columns as ViewColumn[]) : [...DEFAULT_COLUMNS],
          );
        }}
      />

      <TicketCustomerFilter
        customerId={customer ?? ''}
        onChange={(next) => {
          void navigate({
            to: '/app/tickets',
            search: (current) => ({ ...current, customer: next }),
            replace: true,
          });
        }}
      />

      <form
        className="toolbar"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(searchInput.trim());
        }}
      >
        <input
          type="search"
          className="field__control"
          value={searchInput}
          aria-label={t('tickets.searchLabel')}
          placeholder={t('tickets.searchPlaceholder')}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
        />
        <Button type="submit">{t('app.search')}</Button>
        {search ? (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch('');
              setSearchInput('');
            }}
          >
            {t('tickets.clearSearch')}
          </Button>
        ) : null}
      </form>

      <div className="toolbar" role="group" aria-label={t('tickets.state')}>
        {/* Die Reihenfolge ist die der Haeufigkeit, mit der gefragt wird:
            "Offen" zuerst, weil das die Frage ist, mit der man die Liste
            oeffnet -- "Alle" zuletzt, weil man es sucht, wenn man etwas
            Aelteres wiederfinden will (Issue #9). Dazwischen die
            Zustandsgruppen in ihrer fachlichen Ordnung. */}
        {TABS.map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'secondary' : 'ghost'}
            aria-pressed={activeTab === tab}
            onClick={() => {
              setTab(tab);
            }}
          >
            {tab === 'unresolved'
              ? t('tickets.filterUnresolved')
              : tab === 'all'
                ? t('tickets.filterAll')
                : t(`stateCategory.${tab}`)}
            <FilterCount value={counts?.tabs[tab]} />
          </Button>
        ))}

        <span className="shell__spacer" />

        {/* "Nur meine" ist kein Reiter, sondern ein Schalter: Es grenzt
            **quer** zu ihnen ein -- meine offenen, meine geschlossenen --
            und laesst sich deshalb mit jedem von ihnen verbinden. In die
            Reihe gestellt saehe es aus, als schloesse es sie aus. */}
        {/* "Wartet auf uns" steht neben "Nur meine" und aus demselben
            Grund: Es grenzt **quer** zu den Zustandsreitern ein -- meine
            offenen, bei denen jemand wartet -- und ist deshalb ein
            Schalter und kein Reiter (F1-50). */}
        <Button
          variant={awaiting ? 'secondary' : 'ghost'}
          aria-pressed={awaiting === true}
          title={t('tickets.awaitingReplyHint')}
          onClick={() => {
            void navigate({
              to: '/app/tickets',
              search: (current) => ({ ...current, awaiting: awaiting ? undefined : true }),
              replace: true,
            });
          }}
        >
          <Icon name="mailIn" />
          {t('tickets.awaitingReply')}
          <FilterCount value={counts?.awaiting_reply[activeTab]} />
        </Button>

        <Button
          variant={owner === 'me' ? 'secondary' : 'ghost'}
          aria-pressed={owner === 'me'}
          onClick={() => {
            void navigate({
              to: '/app/tickets',
              search: (current) => ({ ...current, owner: owner === 'me' ? undefined : 'me' }),
              replace: true,
            });
          }}
        >
          {t('nav.myTickets')}
          <FilterCount value={counts?.mine[activeTab]} />
        </Button>

        <DensityToggle />
        <ColumnPicker columns={columns} onChange={setColumns} />
      </div>

      {search ? (
        <p className="table__muted" role="status">
          {t('tickets.searchActive', { query: search })}
        </p>
      ) : null}

      {query.isError ? <ErrorNotice error={query.error} /> : null}

      {canWrite ? (
        <BulkBar
          selected={selected}
          onDone={() => {
            setSelected([]);
          }}
        />
      ) : null}

      <div className="card card--flush" aria-busy={query.isPending}>
        {/* Ein Skelett in der Form der Tabelle und kein kreisender Strich:
            Es reserviert den Platz, sodass die Seite beim Eintreffen der
            Daten nicht springt -- und es sagt, was gleich kommt. Die
            Spaltenzahl ist die gewaehlte, die Auswahlspalte zaehlt mit. */}
        {query.isPending ? <SkeletonTable columns={columns.length + (canWrite ? 1 : 0)} /> : null}

        {/* Zwei Leerzustaende und nicht einer: "Noch nichts da" und "nichts
            gefunden" sind verschiedene Lagen, und der Ausweg aus ihnen ist
            ein anderer. Wer nach "Drucker" gesucht hat, braucht keinen Knopf
            zum Anlegen -- er braucht den, der die Suche zuruecknimmt. */}
        {!query.isPending && tickets.length === 0 ? (
          search ? (
            <EmptyState
              icon="search"
              title={t('tickets.noSearchResults')}
              hint={t('tickets.noSearchResultsHint', { query: search })}
              action={
                <Button
                  onClick={() => {
                    setSearch('');
                    setSearchInput('');
                  }}
                >
                  {t('tickets.clearSearch')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon="inbox"
              title={t('tickets.empty')}
              hint={t('tickets.emptyHint')}
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialogOpen(true);
                  }}
                >
                  {t('tickets.newTicket')}
                </Button>
              }
            />
          )
        ) : null}

        {tickets.length > 0 ? (
          <div className="table-wrapper table-wrapper--tall">
            <table className="table">
              <thead>
                <tr>
                  {/* Die Auswahlspalte erscheint nur, wo auch geaendert
                      werden darf -- F1-20. Ein Kaestchen, das zu nichts
                      fuehrt, ist eine Einladung ins Leere. */}
                  {canWrite ? (
                    <th scope="col">
                      <input
                        type="checkbox"
                        aria-label={t('tickets.bulkSelectAll')}
                        checked={selected.length === tickets.length && tickets.length > 0}
                        onChange={(event) => {
                          setSelected(event.target.checked ? tickets.map((item) => item.id) : []);
                        }}
                      />
                    </th>
                  ) : null}
                  {columns.map((column) => {
                    const field = SORT_FIELD_OF[column];
                    // Nicht sortierbare Ueberschriften bleiben Text. Eine
                    // Schaltflaeche, die nichts tut, ist schlechter als eine
                    // Beschriftung, die es sichtbar nicht kann.
                    if (field === undefined) {
                      return (
                        <th key={column} scope="col">
                          {t(`columns.${column}`)}
                        </th>
                      );
                    }

                    const active = sort === field || sort === `-${field}`;
                    const descending = sort === `-${field}`;
                    return (
                      <th
                        key={column}
                        scope="col"
                        // ``aria-sort`` sagt einem vorlesenden Programm, wonach
                        // die Tabelle geordnet ist. Der Pfeil daneben sagt es
                        // dem, der sie sieht -- beide sagen dasselbe, und
                        // keiner von beiden sagt es allein.
                        aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
                      >
                        <button
                          type="button"
                          className="table__sort"
                          aria-label={t(
                            active && descending
                              ? 'tickets.sortAscending'
                              : 'tickets.sortDescending',
                            { column: t(`columns.${column}`) },
                          )}
                          onClick={() => {
                            setSort(field);
                          }}
                        >
                          {t(`columns.${column}`)}
                          <span aria-hidden="true" className="table__sort-marker">
                            {active ? (descending ? '↓' : '↑') : ''}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    data-clickable=""
                    // F1-50: ein Streifen an der Kante. Farbe traegt die
                    // Aussage nie allein -- das Kennzeichen am Betreff sagt
                    // dasselbe in Worten.
                    className={ticket.awaiting_reply_since ? 'is-waiting' : undefined}
                    aria-selected={selected.includes(ticket.id)}
                    onClick={(event) => {
                      // Die ganze Zeile fuehrt zum Ticket -- aber nur, wo
                      // nicht schon etwas anderes darunterliegt. Ohne diese
                      // Pruefung oeffnete ein Klick auf das Auswahlkaestchen
                      // das Ticket, und ein Klick auf den Kundennamen fuehrte
                      // an zwei Stellen gleichzeitig.
                      if ((event.target as HTMLElement).closest('a, button, input, label')) return;
                      // Wer Text markiert, will ihn kopieren und nicht
                      // navigieren.
                      if ((window.getSelection()?.toString().length ?? 0) > 0) return;
                      void navigate({
                        to: '/app/tickets/$ticketId',
                        params: { ticketId: ticket.id },
                      });
                    }}
                  >
                    {canWrite ? (
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('tickets.bulkSelect', { number: ticket.ticket_number })}
                          checked={selected.includes(ticket.id)}
                          onChange={(event) => {
                            setSelected((current) =>
                              event.target.checked
                                ? [...current, ticket.id]
                                : current.filter((value) => value !== ticket.id),
                            );
                          }}
                        />
                      </td>
                    ) : null}
                    {columns.map((column) => (
                      <td key={column}>
                        {renderCell(ticket, column, t, i18n.language, users.data)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {query.hasNextPage ? (
        <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--space-4)' }}>
          <Button
            busy={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            {t('tickets.loadMore')}
          </Button>
        </div>
      ) : null}

      <CreateTicketDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialCustomerId={customer}
      />
    </>
  );
}

/**
 * Die Zahl an einem Reiter oder Schalter der Ticketliste.
 *
 * **Auch die Null steht da.** An den Reitern eines Tickets fehlt sie, weil
 * dort "leer" die Auskunft ist. Hier ist es die Zahl selbst: "Wartet auf uns
 * 0" ist die gute Nachricht, und eine Zahl, die bei null verschwindet, laesst
 * offen, ob nichts da ist oder nichts geladen wurde. Solange nichts geladen
 * ist, steht deshalb auch nichts da.
 *
 * Die Zahl gehoert zum Namen des Knopfes ("Offen 12"): Wer vorlesen laesst,
 * soll sie ebenso hoeren, wie andere sie sehen.
 */
function FilterCount({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  // Das Leerzeichen ist fuer die Sprachausgabe: Ohne es hiesse der Knopf
  // "Offen12".
  return (
    <>
      {' '}
      <span className="tabs__count">{value}</span>
    </>
  );
}

/**
 * Stellt eine Zelle dar.
 *
 * Eine Funktion statt einer Verzweigung im JSX: Die Spaltenwahl ist frei, und
 * fuenfzehn Faelle im Rumpf einer Tabellenzeile waeren nicht mehr zu lesen.
 */
function renderCell(
  ticket: TicketListItem,
  column: ViewColumn,
  t: (key: string) => string,
  language: string,
  users: AssignableUser[] | undefined,
) {
  switch (column) {
    case 'ticket_number':
      return (
        <Link
          to="/app/tickets/$ticketId"
          params={{ ticketId: ticket.id }}
          className="table__number"
        >
          {ticket.ticket_number}
        </Link>
      );
    case 'title':
      return (
        <>
          <Link
            to="/app/tickets/$ticketId"
            params={{ ticketId: ticket.id }}
            className="table__subject"
          >
            {ticket.title}
          </Link>
          {ticket.is_major_incident ? (
            <>
              {' '}
              <Badge tone="danger" dot>
                {t('tickets.majorIncident')}
              </Badge>
            </>
          ) : null}
          {/* F1-50. Am Betreff und nicht in einer eigenen Spalte: Die
              Spalte laesst sich abwaehlen, und das Kennzeichen soll auch
              dann da sein. Die Zahl erscheint erst ab zwei -- eine "1" an
              jedem wartenden Vorgang waere Zierde. */}
          {ticket.awaiting_reply_since ? (
            <>
              {' '}
              <span className="waiting-chip">
                <Icon name="mailIn" />
                {t('tickets.customerReply')}
                {ticket.unanswered_count > 1 ? (
                  <span className="waiting-chip__count">{ticket.unanswered_count}</span>
                ) : null}
              </span>
            </>
          ) : null}
        </>
      );
    case 'customer':
      // K-38: Die Hervorhebung eines bevorzugten Kunden steht an jedem
      // seiner Tickets. Sie kommt aus der Antwort mit und braucht kein
      // Nachladen je Zeile.
      return (
        <>
          <Link to="/app/crm/$customerId" params={{ customerId: ticket.customer.id }}>
            {ticket.customer.name}
          </Link>
          {ticket.customer.is_vip ? (
            <>
              {' '}
              <Badge tone="warning" dot>
                {t('crm.vip')}
              </Badge>
            </>
          ) : null}
        </>
      );
    case 'state':
      return (
        <Badge tone={toneForStateCategory(ticket.state.state_category)} dot>
          {ticket.state.name}
        </Badge>
      );
    case 'priority':
      return (
        <Badge tone={toneForPriorityLevel(ticket.priority.level)}>{ticket.priority.name}</Badge>
      );
    case 'queue':
      return <span className="table__muted">{ticket.queue.name}</span>;
    case 'category':
      return (
        <span className="table__muted">{ticket.category?.name ?? t('tickets.noCategory')}</span>
      );
    case 'owner':
      return (
        <span className="table__muted">
          {nameOf(users, ticket.owner_id) ?? t('tickets.unassigned')}
        </span>
      );
    case 'created_at':
      return <span className="table__muted">{formatDateTime(ticket.created_at, language)}</span>;
    case 'updated_at':
      return <span className="table__muted">{formatDateTime(ticket.updated_at, language)}</span>;
    case 'sla':
      // F4-08: Restzeit statt Faelligkeitszeitpunkt -- siehe SlaBadge.
      return <SlaBadge sla={ticket.sla} />;
    case 'awaiting_reply':
      // F1-50: **seit wann**, nicht wann zuletzt geschrieben wurde. Die
      // Frage lautet "wer wartet am laengsten" -- und nur die laesst sich
      // sortieren.
      return ticket.awaiting_reply_since ? (
        <span className="waiting-age">{formatDateTime(ticket.awaiting_reply_since, language)}</span>
      ) : (
        <span className="table__muted">—</span>
      );
    case 'is_major_incident':
      return ticket.is_major_incident ? (
        <Badge tone="danger" dot>
          {t('tickets.majorIncident')}
        </Badge>
      ) : (
        <span className="table__muted">{t('masterdata.no')}</span>
      );
  }
}
